"use client";

import Image from "next/image";
import Link from "next/link";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { SectionId } from "@/types/navigation";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (sectionId: SectionId) => void;
}

export function MobileMenu({ isOpen, onClose, onNavigate }: MobileMenuProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="menu"
            className="fixed inset-0 bg-white z-50 md:hidden overflow-y-auto"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <motion.div
                className="flex justify-between items-center p-6 border-b border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center">
                  <Image
                    src="/clairvoyo.svg"
                    alt="Clairvoyo Logo"
                    width={32}
                    height={32}
                    className="mr-2"
                  />
                  <span className="text-xl">Clairvoyo</span>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X size={24} />
                </button>
              </motion.div>

              {/* Menu Items */}
              <motion.div
                className="flex flex-col p-4 space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <button
                  onClick={() => onNavigate("features")}
                  className="text-gray-800 hover:text-blue-500 py-3 text-lg font-medium transition-colors duration-200 w-full text-left"
                >
                  Features
                </button>
                <button
                  onClick={() => onNavigate("pricing")}
                  className="text-gray-800 hover:text-blue-500 py-3 text-lg font-medium transition-colors duration-200 w-full text-left"
                >
                  Pricing
                </button>
                <button
                  onClick={() => onNavigate("faq")}
                  className="text-gray-800 hover:text-blue-500 py-3 text-lg font-medium transition-colors duration-200 w-full text-left"
                >
                  FAQ
                </button>

                <div className="pt-4 space-y-4">
                  <Link
                    href="/signup"
                    className="block w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md text-center transition-colors duration-200 font-medium"
                  >
                    Get Clairvoyo
                  </Link>
                  <Link
                    href="/login"
                    className="block w-full text-gray-800 hover:text-blue-500 px-6 py-3 text-center transition-colors duration-200 font-medium border border-gray-200 rounded-md"
                  >
                    Login
                  </Link>
                </div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            key="overlay"
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
        </>
      )}
    </AnimatePresence>
  );
}
